import { useEffect, useState } from 'react';
import type { CoverBackground } from 'fym-shared/src/music24/coverDoc';
import type { CoverPicture } from '../render/drawBackground';
import { loadCoverPicture } from '../render/loadCoverPicture';

export interface LoadedCoverPicture {
  picture: CoverPicture | null;
  /** True when the picture of the background did not load (a deleted file, no network, no CORS). */
  isFailed: boolean;
}

/** The loaded picture of a background, or null for a colour, a gradient, or while it loads. */
export function useCoverPicture(background: CoverBackground): LoadedCoverPicture {
  const url = background.kind === 'picture' ? background.url : null;
  const [loaded, setLoaded] = useState<{ url: string; picture: CoverPicture | null; isFailed: boolean } | null>(null);

  useEffect(() => {
    if (url === null) {
      return undefined;
    }
    let isCurrent = true;
    loadCoverPicture(url)
      .then((picture) => {
        if (isCurrent) {
          setLoaded({ url, picture, isFailed: false });
        }
      })
      .catch((error: unknown) => {
        console.warn('Cover editor: the picture did not load', error);
        if (isCurrent) {
          setLoaded({ url, picture: null, isFailed: true });
        }
      });
    return () => {
      isCurrent = false;
    };
  }, [url]);

  if (url === null || loaded === null || loaded.url !== url) {
    return { picture: null, isFailed: false };
  }
  return { picture: loaded.picture, isFailed: loaded.isFailed };
}
